import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Code, Database, Network, Cog, ArrowRight } from 'lucide-react';

const ProjectsSection = () => {
  const projectExamples = [
    {
      icon: <Code className="h-12 w-12 text-blue-600" />,
      company: "Metallbau-Unternehmen",
      challenge: "B2B-Kunden benötigten individuelle Preisstrukturen und Freigabeprozesse",
      solution: "Custom Shopware 6 B2B-Portal mit ERP-Integration und Workflow-Engine",
      results: [
        "Automatisierte Preisberechnung nach Kundengruppen",
        "Digitale Freigabeprozesse für Großaufträge",
        "Nahtlose Integration in bestehende Warenwirtschaft"
      ]
    },
    {
      icon: <Network className="h-12 w-12 text-green-600" />,
      company: "Fashion-Retailer",
      challenge: "Multi-Channel-Vertrieb über Shop, Amazon und eBay ohne zentrale Verwaltung",
      solution: "Zentrale E-Commerce-Plattform mit API-basierter Marktplatz-Integration",
      results: [
        "Einheitliche Produktdatenverwaltung für alle Kanäle",
        "Automatische Lagerbestandssynchronisation",
        "Zentrale Auftragsabwicklung und Versandsteuerung"
      ]
    },
    {
      icon: <Database className="h-12 w-12 text-purple-600" />,
      company: "Franchise-System",
      challenge: "50+ Standorte brauchten zentrale Beschaffung mit lokaler Flexibilität",
      solution: "Multi-Mandanten-Portal mit standortspezifischen Katalogen und Budgetverwaltung",
      results: [
        "Zentrale Produktkataloge mit lokalen Anpassungen",
        "Automatisierte Budgetverteilung und -kontrolle",
        "Standortspezifische Bestellfreigaben und Reporting"
      ]
    },
    {
      icon: <Cog className="h-12 w-12 text-orange-600" />,
      company: "Industrieunternehmen",
      challenge: "Komplexe Produktkonfiguration und technische Dokumentation im Vertrieb",
      solution: "Konfigurator-Integration mit automatischer Dokumentenerstellung",
      results: [
        "Interaktiver Produktkonfigurator für komplexe Anlagen",
        "Automatische Generierung technischer Dokumentation",
        "Integration in CAD-Systeme und Produktionsplanung"
      ]
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Realisierte <span className="text-blue-600">Online-Shops</span>
          </h2>
          <p className="text-lg text-gray-600">
            Einblicke in unsere Entwicklungsarbeit für mittelständische Unternehmen. 
            Jedes Projekt mit individuellen Anforderungen und maßgeschneiderten Lösungen.
          </p>
        </div>

        {/* Project Examples */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {projectExamples.map((project, index) => (
            <Card key={index} className="border-0 shadow-lg bg-white h-full">
              <CardContent className="p-8 h-full flex flex-col">
                <div className="flex items-center mb-6">
                  {project.icon}
                  <h3 className="text-xl font-bold ml-4">{project.company}</h3>
                </div>
                
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Anforderung:</h4>
                  <p className="text-gray-600">{project.challenge}</p>
                </div>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-blue-600 mb-2">Technische Lösung:</h4>
                  <p className="text-gray-600">{project.solution}</p>
                </div>
                
                <div className="mt-auto">
                  <h4 className="font-semibold text-green-600 mb-3">Implementierte Features:</h4>
                  <ul className="space-y-2">
                    {project.results.map((result, resultIndex) => (
                      <li key={resultIndex} className="flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                        <span className="text-gray-700">{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">
            Lass uns dein E-Commerce-Projekt besprechen
          </h3>
          <p className="text-lg mb-6 opacity-90">
            Jedes Unternehmen hat individuelle Anforderungen. 
            Wir analysieren deine Bedürfnisse und entwickeln die passende technische Lösung.
          </p>
          <a 
            href="#contact" 
            className="inline-flex items-center bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
          >
            Projektgespräch vereinbaren
            <ArrowRight className="ml-2 h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection; 